import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Checkbox, Input, Label, Radio, Text } from '@pukka/ui-components';

import { AccountDeletionTnC } from './AccountDeletionTnC';

interface AccountDeletionFormProps {
  readonly isSubmitting: boolean;
  readonly onSubmit: (reason: string, notes: string) => void;
}

function AccountDeletionForm({ isSubmitting, onSubmit }: AccountDeletionFormProps) {
  const { t } = useTranslation();
  const [showTnC, setShowTnC] = useState(false);
  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      reason: '',
      notes: '',
      tncAccepted: false,
    },
  });

  const reasonOptions = t('accountDeletion.reasonOptions', { returnObjects: true }) as string[];

  const reasonValue = watch('reason');
  const tncAcceptedValue = watch('tncAccepted');
  const enableSubmit = !!reasonValue && tncAcceptedValue;

  const handleSubmitForm = handleSubmit((data) => {
    const { reason, notes } = data;

    onSubmit(reason, notes);
  });

  return (
    <>
      <Text>
        <Trans>{t('accountDeletion.pleaseNote')}</Trans>
      </Text>
      <form aria-label="account-deletion-form" className="flex flex-col gap-5 text-left" onSubmit={handleSubmitForm}>
        <div className="flex flex-col gap-2.5">
          <Label className="text-lg">{t('accountDeletion.reasonInputLabel')}</Label>
          <Radio.Group orientation="vertical">
            {reasonOptions.map((reason) => (
              <Radio key={reason} id={reason} value={reason} {...register('reason')}>
                {reason}
              </Radio>
            ))}
          </Radio.Group>
        </div>
        <div className="flex flex-col gap-2.5">
          <Label className="text-lg">{t('accountDeletion.notesInputLabel')}</Label>
          <Input placeholder={t('accountDeletion.notesInputPlaceholder')} {...register('notes')} />
        </div>
        <div className="rounded bg-neutral-100 p-4">
          <Checkbox id="tnc" data-testid="tnc-checkbox" {...register('tncAccepted')}>
            <Trans
              i18nKey="accountDeletion.tncCheckbox"
              components={{
                ButtonLink: <Text as="span" onClick={() => setShowTnC(true)} color="blue" className="cursor-pointer underline" />,
              }}
            />
          </Checkbox>
        </div>
        <Button variant="danger" type="submit" disabled={!enableSubmit} loading={isSubmitting}>
          {t('accountDeletion.submitButton')}
        </Button>
      </form>
      <hr />
      <Text variant="caption-1">
        <Trans
          i18nKey="accountDeletion.returnToBackoffice"
          components={{ ButtonLink: <Text as="a" href="/" color="blue" className="hover:underline" /> }}
        />
      </Text>

      <AccountDeletionTnC open={showTnC} onToggleModal={setShowTnC} />
    </>
  );
}

export { AccountDeletionForm };
